import { supabase } from '../../lib/supabase';
import { fetchLatestFortnightBreakdown, formatFortnightRange } from '../market-intelligence/utils';

// Single source of truth for valid grades — shared by RateEntryPanel (submission dropdown) and
// FilterBar (filter dropdown) so the two can never drift apart the way they did before (FilterBar was
// still showing the old S-30/M-31 list after RateEntryPanel's grades were corrected to S1-30/S2-30).
export const GRADES = ['S1-30', 'S2-30', 'M-30', 'L-30'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isoMonthsAgo = (n) => {
  const d = new Date();
  d?.setDate(1);
  d?.setMonth(d?.getMonth() - n);
  return d?.toISOString()?.slice(0, 10);
};

const PAGE_SIZE = 1000;
// Supabase/PostgREST caps unpaginated selects (commonly at 1000 rows) — page through with .range()
// so a >1000-row table (mi_release_orders' trailing-12-month window is ~7,400 rows) doesn't get
// silently truncated.
async function fetchAllPages(build) {
  let rows = [];
  let from = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await build(from, from + PAGE_SIZE - 1);
    if (error) return { data: null, error };
    rows = rows?.concat(data || []);
    if (!data || data?.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return { data: rows, error: null };
}

const firstOf = (arr) => (Array.isArray(arr) && arr?.length > 0 ? arr?.[0] : null);

const DIRECTORY_COLUMNS = 'id, factory_name, plant_name, address, website, phones, emails, operational_status, mill_code, trusted_pincode, source_pincode';

// ─── LATERAL-join replacement ─────────────────────────────────────────────────
// The locked query joins mi_mills -> internal_directory_factories via:
//   left join lateral (
//     select * from internal_directory_factories f
//     where f.mill_code = m.mill_code or f.trusted_pincode = m.pincode or f.source_pincode = m.pincode
//     order by case when f.mill_code = m.mill_code then 1 when f.trusted_pincode = m.pincode then 2 else 3 end,
//              case when f.operational_status = 'Working' then 0 else 1 end
//     limit 1
//   ) f on true
// PostgREST's embedded-resource select() syntax cannot express this: there is no FK between mi_mills
// and internal_directory_factories (checked pg_constraint directly), and the match itself isn't
// FK-shaped anyway — it's an OR across three columns with a priority tiebreak, which embedding doesn't
// support regardless of FKs. Also checked pg_proc for an existing RPC matching this shape (%mill%,
// %factory%, %director%) — nothing exists except an unrelated trigger function. Rather than create a
// new DB function (a schema change beyond what was asked), this fetches both tables in full — both are
// small (626 / 831 rows) and both grant SELECT to anon+authenticated — and replicates the LATERAL
// match + ORDER BY + LIMIT 1 in JS.
const matchDirectoryFactory = (mill, directoryRows) => {
  const candidates = directoryRows?.filter((f) =>
    f?.mill_code === mill?.mill_code || f?.trusted_pincode === mill?.pincode || f?.source_pincode === mill?.pincode
  );
  if (!candidates || candidates?.length === 0) return null;
  const priority = (f) => (f?.mill_code === mill?.mill_code ? 1 : f?.trusted_pincode === mill?.pincode ? 2 : 3);
  const statusRank = (f) => (f?.operational_status === 'Working' ? 0 : 1);
  return [...candidates]?.sort((a, b) => {
    const pd = priority(a) - priority(b);
    return pd !== 0 ? pd : statusRank(a) - statusRank(b);
  })?.[0];
};

// Shared shape-builder — used by both the bulk card fetch and the single-mill share-page fetch, so the
// two views can never drift apart.
const buildCard = ({ mill, factory, roRows, rank, yoyChange, activeSeasons, activeGrades, latestFortnight }) => {
  const sortedRo = [...(roRows || [])]?.sort((a, b) => (a?.period_month < b?.period_month ? -1 : 1));
  const trend12m = sortedRo?.map((r) => Number(r?.allocated_qty_mt) || 0);
  const latestRo = sortedRo?.length > 0 ? sortedRo?.[sortedRo?.length - 1] : null;

  // Same fortnight-replaces-stale-month logic as Mandi's fetchMandiListings (mandi/data.js) —
  // mi_release_orders isn't touched until both fortnights of a month are known, so a newer
  // fortnight can already be public before that month's release-order row exists at all. When
  // that's the case, the fortnight figure REPLACES thisMonthQuota here — FRIC's card has one
  // quota field, same shape as Mandi's quotaQty, not Market Intelligence's dual-tile layout.
  // Same self-disabling guard: compare calendar months (both truncated to the 1st), not raw
  // dates — once this month's fortnights are combined into a single mi_release_orders row,
  // that row becomes latestRo and this stops firing for this mill automatically.
  const monthStart = (dateStr) => `${dateStr.slice(0, 7)}-01`;
  const fortnightQty = latestFortnight?.byMill?.get(mill?.id);
  const fortnightIsNewer = latestFortnight && fortnightQty != null &&
    (!latestRo || monthStart(latestFortnight.periodStart) > latestRo.period_month);

  const thisMonthQuota = fortnightIsNewer ? Number(fortnightQty) : (latestRo ? Number(latestRo?.allocated_qty_mt) || 0 : 0);

  return {
    id: mill?.id, // real mi_mills.id UUID — React key + submit handler's mill_id, never displayed
    millId: mill?.id,
    directoryFactoryId: factory?.id || null, // internal_directory_factories.id — needed by getFactoryDetail/getManagersForFactory
    name: factory?.factory_name || mill?.canonical_name,
    plantName: factory?.plant_name || '—',
    address: factory?.address || null,
    website: factory?.website || null,
    state: mill?.source_state || '—',
    district: mill?.source_district || '—',
    operationalStatus: factory?.operational_status || 'Unknown',
    millCode: mill?.mill_code,
    phone: firstOf(factory?.phones) || '—',
    email: firstOf(factory?.emails) || '—',
    // Every card returned here already satisfies the trailing-12-month active-quota condition —
    // it's baked into the fetch (eligibleMillIds), not a separate flag to compute.
    activeQuotaInTrailing12m: true,
    // Real (currently sparse — mill_price_quotes has few rows) distinct season/grade values this mill
    // has ever had a quote for, across any status. The State/District/Search filters already had real
    // data; Season/Grade were previously cosmetic (no field existed on the card at all, so selecting
    // either always hid every card) — now genuinely wired, just thin on data like everything else that
    // depends on mill_price_quotes in this app.
    activeSeasons: activeSeasons || [],
    activeGrades: activeGrades || [],
    miData: {
      thisMonthQuota,
      // Real period_month (YYYY-MM-DD) the above quota figure actually belongs to — not necessarily
      // the calendar-current month, since mi_release_orders can lag. MIDataPanel labels off this
      // instead of guessing from today's date. Left null when thisMonthQuota is actually a
      // fortnight figure (fortnightIsNewer) — MIDataPanel's formatQuotaMonth renders this as a
      // full calendar month label (e.g. "Sep 2026 Quota"), which would misrepresent a half-month
      // fortnight figure as a full month's, the same conflation Mandi's own comments guard
      // against. quotaFortnightLabel below carries the correct range for a future MIDataPanel edit.
      quotaPeriodMonth: fortnightIsNewer ? null : (latestRo?.period_month || null),
      quotaIsFortnight: fortnightIsNewer,
      quotaFortnightLabel: fortnightIsNewer
        ? (formatFortnightRange(latestFortnight.periodStart, latestFortnight.periodEnd) || latestFortnight.periodLabel)
        : null,
      trend12m,
      stateRank: rank?.stateRank ?? null,
      nationalRank: rank?.nationalRank ?? null,
      yoyChange: yoyChange != null ? Number(yoyChange) : null,
      yoyPositive: yoyChange != null ? Number(yoyChange) >= 0 : null,
    },
  };
};

// ─── Main fetch (card grid) ────────────────────────────────────────────────────
// Cannot be fully tested against a logged-in officer's write path until this runs behind auth (see
// RateEntryPanel's submit handler) — but this READ side is real, populated production data (~530-545
// eligible mills as of last check), unlike mill_price_quotes elsewhere in this app which is still thin.
export const fetchFactoryCards = async () => {
  const windowStart = isoMonthsAgo(12);

  const [millsRes, directoryRes, roRes, rankRes, quotesRes] = await Promise.all([
    supabase?.from('mi_mills')
      ?.select('id, canonical_name, mill_code, pincode, source_state, source_district')
      ?.neq('mill_code', 'NEW'),
    supabase?.from('internal_directory_factories')?.select(DIRECTORY_COLUMNS),
    fetchAllPages((from, to) =>
      supabase?.from('mi_release_orders')
        ?.select('mill_id, period_month, allocated_qty_mt')
        ?.gte('period_month', windowStart)
        ?.range(from, to)
    ),
    supabase?.from('mi_v_mill_rank_latest')?.select('mill_id, state_rank, national_rank'),
    // Real Season/Grade filter data — any status, since a mill "has" a season/grade the moment someone
    // submits it, not only once approved. Table is currently small, so an unfiltered select is fine.
    supabase?.from('mill_price_quotes')?.select('mill_id, season, grade'),
  ]);

  if (millsRes?.error) return { cards: [], error: `mi_mills: ${millsRes?.error?.message}` };
  if (directoryRes?.error) return { cards: [], error: `internal_directory_factories: ${directoryRes?.error?.message}` };
  if (roRes?.error) return { cards: [], error: `mi_release_orders: ${roRes?.error?.message || roRes?.error}` };
  if (rankRes?.error) return { cards: [], error: `mi_v_mill_rank_latest: ${rankRes?.error?.message}` };
  if (quotesRes?.error) return { cards: [], error: `mill_price_quotes: ${quotesRes?.error?.message}` };

  // Same source Market Intelligence's homepage and Mandi (mandi/data.js) use for a newer-than-
  // mi_release_orders fortnight figure. Fetched once here and reused across all mills via
  // latestFortnight.byMill (keyed by mi_mills.id), not per-mill. Never lets a fortnight-fetch
  // failure take down the whole card grid.
  let latestFortnight = null;
  try {
    latestFortnight = await fetchLatestFortnightBreakdown();
  } catch (_e) {
    latestFortnight = null;
  }

  // mi_v_mill_yoy is NOT pre-reduced to one row per mill like mi_v_mill_rank_latest is — it has
  // 57,910 rows across 626 mills (one per mill per period_month). Resolve the single latest
  // period_month first, then fetch only that slice, instead of assuming .eq('mill_id', x) alone
  // would return one row.
  const { data: latestPeriodRow, error: latestPeriodError } = await supabase
    ?.from('mi_v_mill_yoy')?.select('period_month')?.order('period_month', { ascending: false })?.limit(1)?.maybeSingle();
  if (latestPeriodError) return { cards: [], error: `mi_v_mill_yoy: ${latestPeriodError?.message}` };

  const yoyByMill = {};
  if (latestPeriodRow?.period_month) {
    const { data: yoyRows, error: yoyError } = await supabase
      ?.from('mi_v_mill_yoy')?.select('mill_id, yoy_change_pct')?.eq('period_month', latestPeriodRow?.period_month);
    if (yoyError) return { cards: [], error: `mi_v_mill_yoy: ${yoyError?.message}` };
    (yoyRows || [])?.forEach((r) => { yoyByMill[r?.mill_id] = r?.yoy_change_pct; });
  }

  const rankByMill = {};
  (rankRes?.data || [])?.forEach((r) => { rankByMill[r?.mill_id] = { stateRank: r?.state_rank, nationalRank: r?.national_rank }; });

  const roByMill = {};
  (roRes?.data || [])?.forEach((r) => {
    if (!roByMill?.[r?.mill_id]) roByMill[r.mill_id] = [];
    roByMill?.[r?.mill_id]?.push(r);
  });

  const seasonsByMill = {};
  const gradesByMill = {};
  (quotesRes?.data || [])?.forEach((q) => {
    if (!q?.mill_id) return;
    if (q?.season) (seasonsByMill[q.mill_id] ||= new Set())?.add(q?.season);
    if (q?.grade) (gradesByMill[q.mill_id] ||= new Set())?.add(q?.grade);
  });

  // exists (select 1 from mi_release_orders ro where ro.mill_id = m.id and ro.allocated_qty_mt > 0
  //         and ro.period_month >= (current_date - interval '12 months'))
  const eligibleMillIds = new Set(
    (roRes?.data || [])?.filter((r) => Number(r?.allocated_qty_mt) > 0)?.map((r) => r?.mill_id)
  );

  const cards = (millsRes?.data || [])
    ?.filter((m) => eligibleMillIds?.has(m?.id))
    ?.map((m) => ({ mill: m, factory: matchDirectoryFactory(m, directoryRes?.data) }))
    // where (f.operational_status is null or f.operational_status <> 'Closed')
    ?.filter(({ factory }) => !factory || factory?.operational_status !== 'Closed')
    ?.map(({ mill, factory }) => buildCard({
      mill,
      factory,
      roRows: roByMill?.[mill?.id],
      rank: rankByMill?.[mill?.id],
      yoyChange: yoyByMill?.[mill?.id],
      activeSeasons: [...(seasonsByMill?.[mill?.id] || [])],
      activeGrades: [...(gradesByMill?.[mill?.id] || [])],
      latestFortnight,
    }));

  return { cards, error: null };
};

// ─── Single-mill fetch (share/PDF page) ────────────────────────────────────────
// Reuses the exact same matching + shape logic as the bulk fetch above (buildCard/matchDirectoryFactory)
// so the share page can never show different identity/MI data than the card it was opened from.
export const fetchFactoryCardById = async (millId) => {
  if (!millId) return { card: null, error: 'No mill id provided' };
  const windowStart = isoMonthsAgo(12);

  const [millRes, directoryRes, roRes, rankRes, quotesRes] = await Promise.all([
    supabase?.from('mi_mills')
      ?.select('id, canonical_name, mill_code, pincode, source_state, source_district')
      ?.eq('id', millId)?.maybeSingle(),
    supabase?.from('internal_directory_factories')?.select(DIRECTORY_COLUMNS),
    supabase?.from('mi_release_orders')?.select('mill_id, period_month, allocated_qty_mt')
      ?.eq('mill_id', millId)?.gte('period_month', windowStart),
    supabase?.from('mi_v_mill_rank_latest')?.select('mill_id, state_rank, national_rank')?.eq('mill_id', millId)?.maybeSingle(),
    supabase?.from('mill_price_quotes')?.select('mill_id, season, grade')?.eq('mill_id', millId),
  ]);

  if (millRes?.error) return { card: null, error: `mi_mills: ${millRes?.error?.message}` };
  if (!millRes?.data) return { card: null, error: 'Mill not found' };
  if (directoryRes?.error) return { card: null, error: `internal_directory_factories: ${directoryRes?.error?.message}` };
  if (roRes?.error) return { card: null, error: `mi_release_orders: ${roRes?.error?.message}` };
  if (rankRes?.error) return { card: null, error: `mi_v_mill_rank_latest: ${rankRes?.error?.message}` };
  if (quotesRes?.error) return { card: null, error: `mill_price_quotes: ${quotesRes?.error?.message}` };

  // Same fortnight fetch as the bulk card grid above — reused here so the share page can never
  // show different quota data than the card it was opened from.
  let latestFortnight = null;
  try {
    latestFortnight = await fetchLatestFortnightBreakdown();
  } catch (_e) {
    latestFortnight = null;
  }

  const { data: latestPeriodRow, error: latestPeriodError } = await supabase
    ?.from('mi_v_mill_yoy')?.select('period_month')?.order('period_month', { ascending: false })?.limit(1)?.maybeSingle();
  if (latestPeriodError) return { card: null, error: `mi_v_mill_yoy: ${latestPeriodError?.message}` };

  let yoyChange = null;
  if (latestPeriodRow?.period_month) {
    const { data: yoyRow, error: yoyError } = await supabase
      ?.from('mi_v_mill_yoy')?.select('yoy_change_pct')?.eq('mill_id', millId)?.eq('period_month', latestPeriodRow?.period_month)?.maybeSingle();
    if (yoyError) return { card: null, error: `mi_v_mill_yoy: ${yoyError?.message}` };
    yoyChange = yoyRow?.yoy_change_pct ?? null;
  }

  const factory = matchDirectoryFactory(millRes?.data, directoryRes?.data);
  const card = buildCard({
    mill: millRes?.data,
    factory,
    roRows: roRes?.data,
    rank: rankRes?.data ? { stateRank: rankRes?.data?.state_rank, nationalRank: rankRes?.data?.national_rank } : null,
    yoyChange,
    activeSeasons: [...new Set((quotesRes?.data || [])?.map((q) => q?.season)?.filter(Boolean))],
    activeGrades: [...new Set((quotesRes?.data || [])?.map((q) => q?.grade)?.filter(Boolean))],
    latestFortnight,
  });

  return { card, error: null };
};

// Latest 'live' mill_price_quotes row per grade for this mill — for the share page's "Current Live
// Rate" section. Cannot be fully tested end-to-end until mill_price_quotes has real 'live' rows for a
// mill (currently thin across the whole app) — structurally correct, verified against the real schema.
export const fetchLiveRatesForMill = async (millId) => {
  if (!millId) return { rates: [], error: null };
  const { data, error } = await supabase
    ?.from('mill_price_quotes')
    ?.select('id, grade, rate_excl_gst, rate_incl_gst, payment_date, lifting_date, available_qty, approved_at, submitted_by')
    ?.eq('mill_id', millId)
    ?.eq('status', 'live')
    ?.order('approved_at', { ascending: false });

  if (error) return { rates: [], error: error?.message };

  // One row per grade — the most recently approved.
  const latestByGrade = {};
  (data || [])?.forEach((r) => {
    if (!latestByGrade?.[r?.grade]) latestByGrade[r.grade] = r;
  });
  return { rates: Object.values(latestByGrade), error: null };
};

// Withdraw (soft-delete) a single live grade offer — same operation My Submissions'
// Expired-tab Withdraw button performs. Any factory_relations_officer can withdraw any
// live row (mpq_officer_withdraw_own is deliberately unscoped to submitted_by — see
// migration 20260902235000_widen_officer_withdraw_to_any_live_row.sql). Explicitly
// checks the returned row count: a blocked RLS write returns no error, just 0 rows
// affected, which would otherwise silently look like nothing happened.
export const withdrawLiveRate = async (quoteId) => {
  const { data: { user } } = await supabase?.auth?.getUser();
  if (!user) return { error: 'Not signed in — cannot withdraw' };

  const { data, error } = await supabase
    ?.from('mill_price_quotes')
    ?.update({ status: 'withdrawn', withdrawn_by: user?.id, withdrawn_at: new Date()?.toISOString() })
    ?.eq('id', quoteId)
    ?.eq('status', 'live')
    ?.select('id');

  if (error) return { error: error?.message };
  if (!data || data?.length === 0) return { error: 'This offer may have already been updated by someone else.' };
  return { error: null };
};

// ─── Submit for Approval (Layer 3) ────────────────────────────────────────────
// INSERT into mill_price_quotes — requires the signed-in user to hold factory_relations_officer
// (enforced by RLS policy mpq_officer_insert_own: auth_user_role() = 'factory_relations_officer'
// AND submitted_by = auth.uid()). Not double-checked client-side — a non-officer's insert will be
// rejected by RLS and the real Postgres error is surfaced, not swallowed.
export const submitGradeBlock = async ({ millId, season, grade, paymentDate, liftingDate, rateExclGst, availableQty }) => {
  const { data: { user } } = await supabase?.auth?.getUser();
  if (!user) return { error: 'Not signed in — cannot submit' };

  const { error } = await supabase?.from('mill_price_quotes')?.insert({
    mill_id: millId,
    season,
    grade,
    payment_date: paymentDate,
    lifting_date: liftingDate,
    rate_excl_gst: Number(rateExclGst),
    available_qty: Number(availableQty), // required — validated non-empty and > 0 before this is called
    status: 'pending_approval',
    submitted_by: user?.id,
    submitted_at: new Date()?.toISOString(),
  });

  if (error) return { error: error?.message };
  return { error: null };
};
